import { Button, Flex, Group, Text, useMantineTheme } from "@mantine/core";
import {
  AlterationAgreementStatus,
  AlterationAgreementType,
  CreateAlterAgreementRecordInput,
  PaymentFrequency,
  SignedAgreementStatus,
  useCreateAlterAgreementRecordMutation,
  useGetAlterationReasonNamesQuery,
} from "src/generated/graphql";
import DatePicker from "src/components/DatePicker";
import Textarea from "src/components/Textarea/Textarea";
import { number, object, string } from "yup";
import { useForm, yupResolver } from "@mantine/form";
import { Request } from ".";
import NumberInput from "src/components/NumberInput/NumberInput";
import { SHOW_TYPE_PAY } from "src/utils/constants";
import Select from "src/components/Select";
import { AlterAgreementValidation } from "src/modules/Client/ClientDetail/Account/Agreements/EditSignedAgreement/StatusChangeRequest";

interface RequestProps {
  selectedAgreement: Request;
  setIsRequest: (e: boolean) => void;
}

type IFormProps = Omit<CreateAlterAgreementRecordInput, "reason"> & {
  reason: "";
};

const AgreementRequest = ({
  selectedAgreement,
  setIsRequest,
}: RequestProps) => {
  const RequestStatus =
    selectedAgreement.status === SignedAgreementStatus.Frozen
      ? AlterationAgreementType.Freeze
      : AlterationAgreementType.Cancel;

  const theme = useMantineTheme();
  const stepSize =
    selectedAgreement.paymentFrequency === PaymentFrequency.BiWeekly ? 2 : 1;

  const { data: reasons } = useGetAlterationReasonNamesQuery({
    variables: {
      locationId: selectedAgreement.locationId || "",
      type: RequestStatus,
    },
  });

  const [alterAgreementRequest, { loading }] =
    useCreateAlterAgreementRecordMutation({
      onCompleted: () => {
        setIsRequest(false);
      },
    });

  const form = useForm({
    initialValues: {
      signedAgreement: { id: selectedAgreement.id },
      notes: "",
      startFreezeAt: new Date(),
      intervals: stepSize,
      reason: "",
      location: { id: selectedAgreement.locationId },
      alterationAgreementStatus: AlterationAgreementStatus.Requested,
      alterationAgreementType: RequestStatus,
    },
    validate: yupResolver(AlterAgreementValidation),
  });

  const handleSubmit = (values: IFormProps) => {
    selectedAgreement &&
      alterAgreementRequest({
        variables: {
          data: {
            ...values,
            intervals: Number(values?.intervals),
            reason: { id: values.reason },
          },
        },
      });
  };

  const handleIntervalChange = () => {
    const { intervals } = form.values;
    if (stepSize === 2 && intervals % 2) {
      form.setFieldValue("intervals", intervals + 1);
    }
  };

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        handleSubmit(values as IFormProps);
      })}
      style={{
        background: theme.colors.secondary[5],
        padding: "20px 10px ",
        borderRadius: "10px",
      }}
    >
      <Flex justify={"center"} align={"center"} direction={"column"}>
        <Text fw={500} size={18}>
          {RequestStatus} Request{" "}
        </Text>
        <Text>for {selectedAgreement?.name} Membership</Text>
      </Flex>
      {selectedAgreement?.status === SignedAgreementStatus?.Frozen ? (
        <>
          <DatePicker
            dropdownPosition="bottom-start"
            label="Start Date"
            {...form.getInputProps("startFreezeAt")}
            mt={"sm"}
          />

          <Flex gap={"lg"} align={"center"}>
            <NumberInput
              label="Duration"
              {...form.getInputProps("intervals")}
              maxLength={7}
              step={stepSize}
              stepHoldDelay={300}
              stepHoldInterval={100}
              onBlur={handleIntervalChange}
            />

            <Text align="start" mt={"xs"}>
              {SHOW_TYPE_PAY[selectedAgreement.paymentFrequency]}
            </Text>
          </Flex>
        </>
      ) : (
        <DatePicker
          dropdownPosition="bottom-start"
          label="Cancel On"
          {...form.getInputProps("startFreezeAt")}
          mt={"sm"}
        />
      )}

      <Select
        withAsterisk
        data={
          reasons?.alterationReasonMany?.map((val) => ({
            value: val.id,
            label: val.reason!,
          })) || []
        }
        label="Reason"
        {...form.getInputProps("reason")}
      />

      <Textarea withAsterisk label="Notes" {...form.getInputProps("notes")} />

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          loading={loading}
          disabled={loading}
          onClick={() => setIsRequest(false)}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          loading={loading}
          disabled={loading}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Submit
        </Button>
      </Group>
    </form>
  );
};

export default AgreementRequest;
